import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AllCellRenderers, DataEditorCore, getMiddleCenterBias, GridCellKind, ImageWindowLoaderImpl, sprites, } from "@glideapps/glide-data-grid";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { createBandLayout, resizedRows } from "./bands.js";
import { paintCellBorders } from "./cellBorders.js";
import { flashColors as toFlashColors, flashEnded, paintFlash, startFlash, } from "./cellFlash.js";
import { useColumnGhost } from "./columnGhost.js";
import { createCssResolver, resolveCell, resolveTheme } from "./cssTheme.js";
import { withExactCellEditor } from "./exactCellEditor.js";
import { cellSelection, clip, columnSelection, columnsBefore, selectCell, selectColumn } from "./selection.js";
import { applyCellStyle, DESIGN_THEME, SEPARATOR, SEPARATOR_ROW, STRIPE } from "./semantics.js";
import "@artis/ui/tokens.css";
// The editor frame's variables also serve consumers' own editors, so they load with the grid.
import "../src/cellEditor.css";
import "../src/grid.css";
import "@glideapps/glide-data-grid/dist/index.css";
const DEFAULT_THEME = { baseFontStyle: "12px", headerFontStyle: "600 12px" };
const EDITOR_BLOOM = [0, 0];
const DEFAULT_ROW_HEIGHT = 24;
const DEFAULT_HEADER_HEIGHT = 26;
const THEME_ATTRIBUTES = ["data-mode", "class", "style"];
const TRANSPARENT = "rgba(0, 0, 0, 0)";
/** The grid draws every line but the separators' itself, so none runs past the last column. */
const NO_GRID_LINES = { horizontalBorderColor: TRANSPARENT };
/**
 * Glide's header canvas runs one row past the header, which Glide strokes across the grid in its header line
 * colour blended over `bgHeader`. With the line transparent and the background a 1% white, the stroke barely
 * touches that row, leaving it to the headers, which paint their own background. Glide reads colours back
 * through the browser, which rounds alpha to 1/255: any less is 0, which blends to `NaN` and strokes black.
 */
const CLEAR_HEADER_ROW = {
    headerBottomBorderColor: TRANSPARENT,
    bgHeader: "rgba(255, 255, 255, 0.01)",
};
const RESIZE_CURSOR = "row-resize";
const FIXED_EDGE_CURSOR = "pointer";
const GLIDE_MIN_COLUMN_WIDTH = 50;
const GLIDE_MAX_COLUMN_WIDTH = 500;
const EDITOR_METHODS = new Set([
    "appendRow",
    "updateCells",
    "getBounds",
    "focus",
    "emit",
    "scrollTo",
    "remeasureColumns",
]);
const noop = () => undefined;
const paintFill = (ctx, color, x, y, w, h) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
    ctx.restore();
};
const paintColumnLine = (ctx, rect, color) => paintFill(ctx, color, rect.x + rect.width - 1, rect.y, 1, rect.height);
/** Takes the width the columns leave, so nothing the grid draws for them runs past the last one. */
const FILLER = { id: "artis-filler", title: "", width: 0, grow: 1, resizable: false, movable: false };
const FILLER_CELL = {
    kind: GridCellKind.Text,
    data: "",
    displayData: "",
    allowOverlay: false,
    readonly: true,
};
const withoutLine = (theme) => {
    if (!theme?.horizontalBorderColor)
        return theme;
    const rest = { ...theme };
    delete rest.horizontalBorderColor;
    return rest;
};
/**
 * A fresh resolver per colour-mode change, so everything keyed on it re-resolves and repaints.
 * The ref holds it from creation, ahead of the render that mounts Glide and paints its first frame.
 */
const useCssResolver = (probe) => {
    const [resolver, setResolver] = useState(null);
    const resolverRef = useRef(null);
    useLayoutEffect(() => {
        if (!probe)
            return;
        const refresh = () => {
            resolverRef.current = createCssResolver(probe);
            setResolver(resolverRef.current);
        };
        refresh();
        const observer = new MutationObserver(refresh);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: THEME_ATTRIBUTES });
        return () => observer.disconnect();
    }, [probe]);
    return [resolver, resolverRef];
};
const useLatest = (value) => {
    const ref = useRef(value);
    useLayoutEffect(() => {
        ref.current = value;
    });
    return ref;
};
/**
 * Glide writes its own cursor on its scroller and canvas after ours, so the cursor is set again on
 * the next frame.
 */
const setGridCursor = (wrapper, cursor) => {
    const targets = wrapper?.querySelectorAll(".dvn-scroller, [data-testid='data-grid-canvas']") ?? [];
    const apply = () => {
        for (const target of targets)
            target.style.cursor = cursor;
    };
    apply();
    if (cursor)
        requestAnimationFrame(apply);
};
/** Glide resizes every column from its header edge, so presses on a fixed column's edge stop at the wrapper. */
const useFixedEdgePress = (wrapper, onFixedEdgeRef) => {
    useEffect(() => {
        if (!wrapper)
            return;
        const onMouseDown = (e) => {
            if (e.button !== 0 || !onFixedEdgeRef.current)
                return;
            e.preventDefault();
            e.stopPropagation();
        };
        wrapper.addEventListener("mousedown", onMouseDown, { capture: true });
        return () => wrapper.removeEventListener("mousedown", onMouseDown, { capture: true });
    }, [wrapper, onFixedEdgeRef]);
};
/**
 * A capture listener on the wrapper sees the press before Glide, which listens on `window`, and
 * keeps it from starting a range selection.
 */
const useSeparatorDrag = (wrapper, hoveredRef, layoutRef, rowHeightRef, onBandResizeRef) => {
    useEffect(() => {
        if (!wrapper)
            return;
        const onMouseDown = (e) => {
            const row = hoveredRef.current;
            if (e.button !== 0 || row === null)
                return;
            e.preventDefault();
            e.stopPropagation();
            const onBandResize = onBandResizeRef.current;
            const band = layoutRef.current.bandAbove(row);
            if (!band || band.resizable === false || !onBandResize)
                return;
            const step = rowHeightRef.current(layoutRef.current.rowOf(band.id, band.rows - 1));
            let rows = band.rows;
            const onMove = (ev) => {
                const next = resizedRows(band, band.rows, ev.clientY - e.clientY, step);
                if (next === rows)
                    return;
                rows = next;
                onBandResize(band.id, rows, "drag");
            };
            const onUp = () => {
                window.removeEventListener("mousemove", onMove);
                document.body.style.cursor = "";
                onBandResize(band.id, rows, "end");
            };
            document.body.style.cursor = RESIZE_CURSOR;
            window.addEventListener("mousemove", onMove);
            window.addEventListener("mouseup", onUp, { once: true });
        };
        wrapper.addEventListener("mousedown", onMouseDown, { capture: true });
        return () => wrapper.removeEventListener("mousedown", onMouseDown, { capture: true });
    }, [wrapper, hoveredRef, layoutRef, rowHeightRef, onBandResizeRef]);
};
/** Where Glide draws a cell's text, for the flash highlight to sit behind it. */
const flashTarget = ({ ctx, rect, cell, theme }) => {
    const font = `${theme.baseFontStyle} ${theme.fontFamily}`;
    return {
        rect,
        text: "displayData" in cell ? String(cell.displayData) : "",
        align: cell.contentAlign ?? "left",
        padding: theme.cellHorizontalPadding,
        font,
        bias: getMiddleCenterBias(ctx, font),
    };
};
export const ArtisDataGrid = forwardRef(function ArtisDataGrid({ wrapperRef, theme, provideEditor, rows, columns, freezeColumns = 0, rowHeight = DEFAULT_ROW_HEIGHT, bands, onBandResize, getCellContent, getRowThemeOverride, getCellBorders, getCellStyle, striped, drawCell, drawHeader, verticalBorder, onMouseMove, minColumnWidth, maxColumnWidth, onColumnResize, onColumnResizeStart, onColumnResizeEnd, onColumnMoved, onColumnProposeMove, rowMarkers, gridSelection, onGridSelectionChange, onCellsEdited, onCellEdited, onPaste, onCellClicked, onCellActivated, onCellContextMenu, onHeaderClicked, onHeaderContextMenu, highlightRegions, flashColors, fillHandle, onFillPattern, headerIcons, imageWindowLoader, experimental, headerHeight = DEFAULT_HEADER_HEIGHT, ...rest }, ref) {
    const [wrapper, setWrapper] = useState(null);
    const portalRef = useRef(null);
    const editorRef = useRef(null);
    const [probe, setProbe] = useState(null);
    const [resolver, resolverRef] = useCssResolver(probe);
    const layout = useMemo(() => createBandLayout(bands ?? [{ id: "", rows: rows ?? 0 }]), [bands, rows]);
    /** The filler's index, or -1 when a consumer column already grows into the free width. */
    const filler = columns.some((c) => "grow" in c && (c.grow ?? 0) > 0) ? -1 : columns.length;
    const glideColumns = useMemo(() => (filler < 0 ? columns : [...columns, FILLER]), [columns, filler]);
    const contentRowHeight = useMemo(() => (typeof rowHeight === "number" ? () => rowHeight : rowHeight), [rowHeight]);
    const effectiveRowHeight = useMemo(() => bands ? (row) => (layout.isSeparator(row) ? SEPARATOR.height - 1 : contentRowHeight(row)) : rowHeight, [bands, layout, contentRowHeight, rowHeight]);
    const layoutRef = useLatest(layout);
    const rowHeightRef = useLatest(contentRowHeight);
    const onBandResizeRef = useLatest(onBandResize);
    const hoveredSeparator = useRef(null);
    useSeparatorDrag(wrapper, hoveredSeparator, layoutRef, rowHeightRef, onBandResizeRef);
    const onFixedEdge = useRef(false);
    useFixedEdgePress(wrapper, onFixedEdge);
    const cursorOverridden = useRef(false);
    const headerUnderPointer = useRef(null);
    const columnUnderPointer = useRef(null);
    const flashes = useRef(new Map());
    const flashFrame = useRef(null);
    const repaintFlashes = useCallback(function repaint() {
        const now = performance.now();
        const cells = [...flashes.current.values()].map(({ cell }) => ({ cell }));
        for (const [key, { flash }] of flashes.current)
            if (flashEnded(flash, now))
                flashes.current.delete(key);
        editorRef.current?.updateCells(cells);
        if (flashes.current.size > 0)
            flashFrame.current = requestAnimationFrame(repaint);
        else
            flashFrame.current = null;
    }, []);
    const flashCells = useCallback((cells) => {
        const now = performance.now();
        for (const { cell, delta } of cells) {
            const key = `${cell[0]}:${cell[1]}`;
            const flash = startFlash(flashes.current.get(key)?.flash, now);
            flashes.current.set(key, { cell, flash: delta ? { ...flash, delta } : flash });
        }
        flashFrame.current ??= requestAnimationFrame(repaintFlashes);
    }, [repaintFlashes]);
    useEffect(() => () => {
        if (flashFrame.current !== null)
            cancelAnimationFrame(flashFrame.current);
    }, []);
    useImperativeHandle(ref, () => new Proxy({}, {
        get: (_, key) => {
            if (key === "resolveColor")
                return (value) => resolverRef.current?.color(value) ?? value;
            if (key === "locate")
                return (row) => layoutRef.current.locate(row);
            if (key === "rowOf")
                return (band, row) => layoutRef.current.rowOf(band, row);
            if (key === "flashCells")
                return flashCells;
            return editorRef.current?.[key] ?? (EDITOR_METHODS.has(key) ? noop : undefined);
        },
    }), [resolverRef, layoutRef, flashCells]);
    const mergedTheme = useMemo(() => {
        if (!resolver)
            return null;
        const base = { ...DESIGN_THEME, ...DEFAULT_THEME, ...theme };
        return { ...resolveTheme({ ...base, ...NO_GRID_LINES }, resolver), ...CLEAR_HEADER_ROW };
    }, [theme, resolver]);
    const headerBackground = useMemo(() => resolver?.color({ ...DESIGN_THEME, ...DEFAULT_THEME, ...theme }.bgHeader ?? "") ?? "", [theme, resolver]);
    const baseFontStyle = theme?.baseFontStyle ?? DEFAULT_THEME.baseFontStyle ?? "";
    const editor = useMemo(() => (provideEditor ? withExactCellEditor(provideEditor) : undefined), [provideEditor]);
    /** Separator rows hold one read-only cell per side of the frozen columns; Glide keeps grid lines out of spans. */
    const separatorCell = useCallback((col) => ({
        kind: GridCellKind.Text,
        data: "",
        displayData: "",
        allowOverlay: false,
        readonly: true,
        span: col < freezeColumns ? [0, freezeColumns - 1] : [freezeColumns, columns.length - 1],
        themeOverride: { bgCell: SEPARATOR.color },
    }), [freezeColumns, columns.length]);
    const resolvedGetCellContent = useCallback((item) => {
        if (item[0] === filler)
            return FILLER_CELL;
        const at = layout.locate(item[1]);
        if (!at)
            return resolver ? resolveCell(separatorCell(item[0]), resolver) : separatorCell(item[0]);
        const cell = getCellContent(item, at);
        const style = getCellStyle?.(item, at);
        const styled = style ? applyCellStyle(cell, style, baseFontStyle) : cell;
        return resolver ? resolveCell(styled, resolver) : styled;
    }, [filler, layout, separatorCell, getCellContent, getCellStyle, baseFontStyle, resolver]);
    const resolvedGetRowThemeOverride = useMemo(() => striped || getRowThemeOverride || bands
        ? (row) => {
            if (row >= layout.total)
                return undefined;
            const at = layout.locate(row);
            if (!at)
                return resolver ? resolveTheme(SEPARATOR_ROW, resolver) : SEPARATOR_ROW;
            const stripe = striped && layout.dataIndex(row) % 2 === 1 ? STRIPE : undefined;
            const own = withoutLine(getRowThemeOverride?.(row, at));
            const below = row > 0 && layout.isSeparator(row - 1) ? SEPARATOR_ROW : undefined;
            const merged = { ...stripe, ...own, ...below };
            const override = Object.keys(merged).length > 0 ? merged : undefined;
            return override && resolver ? resolveTheme(override, resolver) : override;
        }
        : undefined, [striped, getRowThemeOverride, bands, layout, resolver]);
    /** Repaints the given headers on the next frame, gathering every request made before it. */
    const damagedHeaders = useRef(new Set());
    const headerFrame = useRef(null);
    const repaintHeaders = useCallback((cols) => {
        for (const col of cols)
            damagedHeaders.current.add(col);
        headerFrame.current ??= requestAnimationFrame(() => {
            headerFrame.current = null;
            const cells = [...damagedHeaders.current].map((col) => ({ cell: [col, -1] }));
            damagedHeaders.current.clear();
            editorRef.current?.updateCells(cells);
        });
    }, []);
    useEffect(() => () => {
        if (headerFrame.current !== null)
            cancelAnimationFrame(headerFrame.current);
    }, []);
    /** Glide repaints a header only when the hovered column changes, so moves within one repaint it here. */
    const headerPointer = useRef(null);
    const trackHeaderPointer = useCallback((args) => {
        const prev = headerPointer.current;
        const next = args && args.location[0] >= 0 ? { col: args.location[0], x: args.localEventX, y: args.localEventY } : null;
        headerPointer.current = next;
        const cols = [prev?.col, next?.col].filter((col) => col !== undefined);
        if (cols.length > 0)
            repaintHeaders(cols);
    }, [repaintHeaders]);
    useEffect(() => {
        if (!wrapper || !drawHeader)
            return;
        const onLeave = () => trackHeaderPointer(undefined);
        wrapper.addEventListener("mouseleave", onLeave);
        return () => wrapper.removeEventListener("mouseleave", onLeave);
    }, [wrapper, drawHeader, trackHeaderPointer]);
    const columnLineColor = mergedTheme?.borderColor ?? "";
    const plainColor = mergedTheme?.bgCell ?? "";
    /** The row line colour Glide would have drawn, before `NO_GRID_LINES` switched its own off. */
    const rowLineColor = useMemo(() => {
        const base = { ...DESIGN_THEME, ...theme };
        return resolver?.color(base.horizontalBorderColor ?? base.borderColor ?? "") ?? "";
    }, [theme, resolver]);
    /** The line along a row's bottom: the next row's line colour, or none where Glide draws a separator's. */
    const lineBelow = useCallback((row) => {
        const next = row + 1;
        if (layout.isSeparator(next))
            return undefined;
        const at = next < layout.total ? layout.locate(next) : undefined;
        const own = at && getRowThemeOverride?.(next, at)?.horizontalBorderColor;
        return own && resolver ? resolver.color(own) : rowLineColor;
    }, [layout, getRowThemeOverride, resolver, rowLineColor]);
    const hasColumnLine = useCallback((col) => (typeof verticalBorder === "function" ? verticalBorder(col + 1) : (verticalBorder ?? true)), [verticalBorder]);
    /**
     * Content, then custom drawing, then borders on top. Separator rows are repainted plain, since
     * Glide tints a span whenever a selection touches it.
     */
    const resolvedFlashColors = useMemo(() => {
        const colors = toFlashColors(flashColors);
        return resolver
            ? {
                highlight: resolver.color(colors.highlight),
                up: resolver.color(colors.up),
                down: resolver.color(colors.down),
            }
            : colors;
    }, [flashColors, resolver]);
    const decoratedDrawCell = useMemo(() => resolver
        ? (args, drawContent) => {
            const { ctx, rect, col, row } = args;
            const at = layout.locate(row);
            if (!at)
                return paintFill(ctx, resolver.color(SEPARATOR.color), rect.x, rect.y, rect.width, rect.height);
            if (col === filler)
                return paintFill(ctx, plainColor, rect.x, rect.y, rect.width, rect.height);
            const flash = flashes.current.get(`${col}:${row}`)?.flash;
            const target = flash && flashTarget(args);
            const now = performance.now();
            if (flash && target)
                paintFlash(ctx, target, flash, now, resolvedFlashColors, "highlight");
            if (drawCell)
                drawCell(args, drawContent);
            else
                drawContent();
            if (flash && target)
                paintFlash(ctx, target, flash, now, resolvedFlashColors, "delta");
            if (getCellBorders) {
                const borders = getCellBorders([col, row], at);
                if (borders)
                    paintCellBorders(ctx, rect, borders, resolver.color);
                return;
            }
            const below = args.cell?.themeOverride?.horizontalBorderColor ?? lineBelow(row);
            if (below)
                paintFill(ctx, below, rect.x, rect.y + rect.height - 1, rect.width, 1);
            if (hasColumnLine(col))
                paintColumnLine(ctx, rect, columnLineColor);
        }
        : drawCell, [
        filler,
        plainColor,
        lineBelow,
        getCellBorders,
        layout,
        drawCell,
        resolver,
        resolvedFlashColors,
        hasColumnLine,
        columnLineColor,
    ]);
    /** Glide counts the row-number column when proposing a move but not when reporting it, and reports refused drops too. */
    const markerOffset = (typeof rowMarkers === "object" ? rowMarkers.kind : (rowMarkers ?? "none")) === "none" ? 0 : 1;
    /** The frozen headers' right edge, which clips the scrolled ones; Glide draws the frozen headers first. */
    const frozenEdge = useRef(0);
    const frozenCount = freezeColumns + markerOffset;
    /**
     * Every header covers the extra row Glide's header canvas has under it: Glide's clip is lifted (its own
     * `restore` balances the `save`) and redrawn a pixel taller, still right of the frozen headers. Glide's own
     * and the row-number header get their background, states included, and column line; a custom header draws
     * its own.
     */
    const decoratedDrawHeader = useMemo(() => resolver
        ? (glideArgs, drawContent) => {
            const { ctx, rect: glideRect } = glideArgs;
            const rect = { ...glideRect, height: glideRect.height + 1 };
            const args = { ...glideArgs, rect };
            // Glide hands over its mapped column, which knows whether it is frozen.
            const sticky = glideArgs.column?.sticky === true;
            if (sticky)
                frozenEdge.current = rect.x + rect.width;
            const left = sticky || frozenCount === 0 ? rect.x : Math.max(rect.x, frozenEdge.current);
            ctx.restore();
            ctx.save();
            ctx.beginPath();
            ctx.rect(left, rect.y, rect.x + rect.width - left, rect.height);
            ctx.clip();
            if (args.columnIndex === filler)
                return paintFill(ctx, plainColor, rect.x, rect.y, rect.width, rect.height);
            if (drawHeader && args.columnIndex >= 0) {
                const pointer = headerPointer.current;
                drawHeader({
                    ...args,
                    column: args.column,
                    mouse: pointer?.col === args.columnIndex ? { x: pointer.x, y: pointer.y } : undefined,
                    repaint: () => repaintHeaders([args.columnIndex]),
                    resolveColor: resolver.color,
                }, drawContent);
                return;
            }
            const { theme: glideTheme, isSelected, hasSelectedCell, hoverAmount } = args;
            const background = isSelected
                ? glideTheme.accentColor
                : hasSelectedCell
                    ? glideTheme.bgHeaderHasFocus
                    : headerBackground;
            paintFill(ctx, background, rect.x, rect.y, rect.width, rect.height);
            if (!isSelected && hoverAmount > 0) {
                ctx.globalAlpha = hoverAmount;
                paintFill(ctx, glideTheme.bgHeaderHovered, rect.x, rect.y, rect.width, rect.height);
                ctx.globalAlpha = 1;
            }
            drawContent();
            if (hasColumnLine(args.columnIndex))
                paintColumnLine(ctx, rect, columnLineColor);
        }
        : undefined, [
        filler,
        plainColor,
        frozenCount,
        headerBackground,
        resolver,
        drawHeader,
        repaintHeaders,
        hasColumnLine,
        columnLineColor,
    ]);
    /**
     * Glide never hands row markers to `drawCell`, so their renderer is wrapped instead. The grid
     * renders Glide's core editor to pass renderers, with the icons and image loader its default
     * editor adds.
     */
    const icons = useMemo(() => ({ ...sprites, ...headerIcons }), [headerIcons]);
    const imageLoader = useMemo(() => imageWindowLoader ?? new ImageWindowLoaderImpl(), [imageWindowLoader]);
    const renderers = useMemo(() => resolver
        ? AllCellRenderers.map((renderer) => renderer.kind === "marker"
            ? {
                ...renderer,
                draw: (args, cell) => {
                    const { ctx, rect, row } = args;
                    if (layout.isSeparator(row)) {
                        paintFill(ctx, resolver.color(SEPARATOR.color), rect.x, rect.y, rect.width, rect.height);
                        return;
                    }
                    if ("row" in cell) {
                        const numbered = { ...cell, row: cell.row - row + layout.dataIndex(row) };
                        renderer.draw({ ...args, cell: numbered }, numbered);
                    }
                    else
                        renderer.draw(args, cell);
                    const below = getCellBorders ? rowLineColor : lineBelow(row);
                    if (below)
                        paintFill(ctx, below, rect.x, rect.y + rect.height - 1, rect.width, 1);
                    paintColumnLine(ctx, rect, columnLineColor);
                },
            }
            : renderer)
        : AllCellRenderers, [getCellBorders, layout, resolver, rowLineColor, lineBelow, columnLineColor]);
    const trackHover = useCallback((args) => {
        const row = args.kind === "cell" ? args.location[1] : -1;
        const onSeparator = args.kind === "cell" && layout.isSeparator(row);
        const resizable = onSeparator && !!onBandResize && layout.bandAbove(row)?.resizable !== false;
        const fixedEdge = args.kind === "header" && args.isEdge && glideColumns[args.location[0]]?.resizable === false;
        const cursor = resizable ? RESIZE_CURSOR : fixedEdge ? FIXED_EDGE_CURSOR : "";
        if (cursor || cursorOverridden.current)
            setGridCursor(wrapper, cursor);
        cursorOverridden.current = cursor !== "";
        hoveredSeparator.current = onSeparator ? row : null;
        onFixedEdge.current = fixedEdge;
        headerUnderPointer.current =
            args.kind === "header" ? { col: args.location[0], bounds: args.bounds, edge: args.isEdge } : null;
        columnUnderPointer.current = args.kind === "header" || args.kind === "cell" ? args.location[0] : null;
        if (drawHeader)
            trackHeaderPointer(args.kind === "header" ? args : undefined);
        onMouseMove?.(args);
    }, [onBandResize, layout, glideColumns, wrapper, drawHeader, trackHeaderPointer, onMouseMove]);
    const resize = useMemo(() => {
        const min = minColumnWidth ?? GLIDE_MIN_COLUMN_WIDTH;
        const max = maxColumnWidth ?? GLIDE_MAX_COLUMN_WIDTH;
        const limited = (callback) => callback &&
            ((column, width, index, widthWithGrow) => {
                if (column.resizable === false)
                    return;
                const clamped = Math.min(column.maxWidth ?? max, Math.max(column.minWidth ?? min, width));
                callback(column, clamped, index, widthWithGrow - width + clamped);
            });
        return {
            minColumnWidth: Math.min(min, ...columns.map((c) => c.minWidth ?? min)),
            maxColumnWidth: Math.max(max, ...columns.map((c) => c.maxWidth ?? max)),
            onColumnResize: limited(onColumnResize),
            onColumnResizeStart: limited(onColumnResizeStart),
            onColumnResizeEnd: limited(onColumnResizeEnd),
        };
    }, [minColumnWidth, maxColumnWidth, columns, onColumnResize, onColumnResizeStart, onColumnResizeEnd]);
    const moveAllowed = useCallback((from, to) => {
        if (filler >= 0 && Math.max(from, to) >= filler)
            return false;
        for (let i = Math.min(from, to); i <= Math.max(from, to); i++)
            if (columns[i]?.movable === false)
                return false;
        return onColumnProposeMove?.(from, to) ?? true;
    }, [filler, columns, onColumnProposeMove]);
    const moveAllowedRef = useLatest(moveAllowed);
    useColumnGhost(wrapper, !!onColumnMoved, headerUnderPointer, columnUnderPointer, moveAllowedRef);
    const reorder = useMemo(() => ({
        onColumnProposeMove: (from, to) => moveAllowed(from - markerOffset, to - markerOffset),
        onColumnMoved: onColumnMoved &&
            ((from, to) => {
                if (moveAllowed(from, to))
                    onColumnMoved(from, to);
            }),
    }), [moveAllowed, markerOffset, onColumnMoved]);
    /**
     * Glide draws grid lines over span edges, the frozen boundary and the row-number edge, after the
     * cells; outlines come later still, so one in the separator colour hides those lines.
     */
    const regionsWithSeparators = useMemo(() => bands && resolver
        ? [
            ...layout.separatorRows.map((y) => ({
                color: resolver.color(SEPARATOR.color),
                style: "solid",
                range: { x: 0, y, width: columns.length, height: 1 },
            })),
            ...(highlightRegions ?? []),
        ]
        : highlightRegions, [bands, resolver, layout, columns.length, highlightRegions]);
    /**
     * The grid holds the selection unless the consumer controls it, so a cursor landing on a
     * separator moves on past it, and the fill handle and event handlers see it.
     */
    const [ownSelection, setOwnSelection] = useState(undefined);
    const selection = gridSelection ?? ownSelection;
    const changeSelection = useCallback((proposed) => {
        const next = filler > 0 ? columnsBefore(proposed, filler) : proposed;
        const current = next.current;
        const row = current?.cell[1];
        let stepped = next;
        if (current && row !== undefined && layout.isSeparator(row)) {
            const from = selection?.current?.cell[1] ?? row - 1;
            const target = layout.nearestDataRow(row, row >= from ? 1 : -1);
            const single = current.range.width === 1 && current.range.height === 1;
            stepped = {
                ...next,
                current: {
                    ...current,
                    cell: [current.cell[0], target],
                    range: single ? { ...current.range, y: target } : current.range,
                },
            };
        }
        if (gridSelection === undefined)
            setOwnSelection(stepped);
        onGridSelectionChange?.(stepped);
    }, [filler, layout, selection, gridSelection, onGridSelectionChange]);
    const selectionRef = useLatest(selection);
    const changeSelectionRef = useLatest(changeSelection);
    const range = selection?.current?.range;
    const showFillHandle = typeof fillHandle === "function"
        ? !!range && fillHandle(range, layout.locateSpan(range.y, range.height))
        : fillHandle;
    const locatedFillPattern = useMemo(() => onFillPattern &&
        ((event) => {
            const { patternSource: source, fillDestination: proposed } = event;
            const destination = filler > 0 ? (clip(proposed, filler) ?? proposed) : proposed;
            onFillPattern(destination === proposed ? event : { ...event, fillDestination: destination }, {
                source: layout.locateSpan(source.y, source.height),
                destination: layout.locateSpan(destination.y, destination.height),
            });
        }), [onFillPattern, filler, layout]);
    const located = useMemo(() => {
        const at = (cell) => (cell[0] === filler ? undefined : layout.locate(cell[1]));
        const apply = (next) => next && changeSelectionRef.current(next);
        const cellInfo = (cell) => {
            const selection = selectionRef.current;
            const { selected, range } = cellSelection(selection, cell);
            return {
                selection,
                selected,
                range: range && { rect: range, at: layout.locateSpan(range.y, range.height) },
                select: () => apply(selectCell(selectionRef.current, cell)),
            };
        };
        const headerInfo = (col) => ({
            selection: selectionRef.current,
            selected: columnSelection(selectionRef.current, col),
            select: () => apply(selectColumn(selectionRef.current, col)),
        });
        return {
            onCellsEdited: onCellsEdited &&
                ((edits) => onCellsEdited(edits.flatMap((edit) => {
                    const location = at(edit.location);
                    return location ? [{ ...edit, at: location }] : [];
                }))),
            onCellEdited: onCellEdited &&
                ((cell, value) => {
                    const location = at(cell);
                    if (location)
                        onCellEdited(cell, value, location);
                }),
            onPaste: typeof onPaste === "function"
                ? (target, values) => {
                    const location = at(target);
                    return location ? onPaste(target, values, location) : false;
                }
                : onPaste,
            onCellClicked: onCellClicked &&
                ((cell, event) => {
                    const location = at(cell);
                    if (location)
                        onCellClicked(cell, { ...event, ...cellInfo(cell) }, location);
                }),
            onCellActivated: onCellActivated &&
                ((cell) => {
                    const location = at(cell);
                    if (location)
                        onCellActivated(cell, location);
                }),
            onCellContextMenu: onCellContextMenu &&
                ((cell, event) => {
                    const location = at(cell);
                    if (location)
                        onCellContextMenu(cell, { ...event, ...cellInfo(cell) }, location);
                }),
            onHeaderClicked: onHeaderClicked &&
                ((col, event) => {
                    if (col !== filler)
                        onHeaderClicked(col, { ...event, ...headerInfo(col) });
                }),
            onHeaderContextMenu: onHeaderContextMenu &&
                ((col, event) => {
                    if (col !== filler)
                        onHeaderContextMenu(col, { ...event, ...headerInfo(col) });
                }),
        };
    }, [
        filler,
        layout,
        selectionRef,
        changeSelectionRef,
        onCellsEdited,
        onCellEdited,
        onPaste,
        onCellClicked,
        onCellActivated,
        onCellContextMenu,
        onHeaderClicked,
        onHeaderContextMenu,
    ]);
    useEffect(() => {
        const portal = portalRef.current;
        if (!wrapper || !portal)
            return;
        const onWheel = (e) => {
            if (portal.childElementCount > 0) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        wrapper.addEventListener("wheel", onWheel, { passive: false, capture: true });
        return () => wrapper.removeEventListener("wheel", onWheel, { capture: true });
    }, [wrapper]);
    return (_jsxs("div", { ref: (el) => {
            setWrapper(el);
            wrapperRef?.(el);
        }, "data-artis-ui": true, "data-artis-grid": true, style: { width: "100%", height: "100%" }, children: [_jsx("span", { ref: setProbe, hidden: true }), mergedTheme && (_jsx(DataEditorCore, { ref: editorRef, headerIcons: icons, imageWindowLoader: imageLoader, theme: mergedTheme, provideEditor: editor, columns: glideColumns, freezeColumns: freezeColumns, rows: layout.total, rowHeight: effectiveRowHeight, getCellContent: resolvedGetCellContent, getRowThemeOverride: resolvedGetRowThemeOverride, drawCell: decoratedDrawCell, drawHeader: decoratedDrawHeader, renderers: renderers, verticalBorder: false, onMouseMove: trackHover, gridSelection: selection, onGridSelectionChange: changeSelection, fillHandle: showFillHandle, onFillPattern: locatedFillPattern, ...located, ...resize, ...reorder, rowMarkers: rowMarkers, highlightRegions: regionsWithSeparators, editorBloom: EDITOR_BLOOM, headerHeight: headerHeight - 1, smoothScrollX: true, smoothScrollY: true, width: "100%", height: "100%", experimental: { ...experimental, disableMinimumCellWidth: true }, ...rest })), _jsx("div", { id: "portal", ref: portalRef, style: { position: "fixed", top: 0, left: 0, zIndex: 9999 } })] }));
});
//# sourceMappingURL=ArtisDataGrid.js.map